/**
 * Invite Filters Component
 *
 * Search and filter controls for invite list.
 *
 * @fileoverview Invite filters component
 */

import React from 'react';
import {
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdSearch, MdFilterList, MdClear } from 'react-icons/md';
import type { InviteCombinedStatus } from '../../types/admin';

interface InviteFiltersProps {
  search: string;
  combinedStatus: InviteCombinedStatus | undefined;
  onSearchChange: (value: string) => void;
  onCombinedStatusChange: (value: InviteCombinedStatus | undefined) => void;
  onReset: () => void;
}

/**
 * Invite Filters Component
 *
 * Provides search and filter controls for the invite list.
 */
export const InviteFilters: React.FC<InviteFiltersProps> = ({
  search,
  combinedStatus,
  onSearchChange,
  onCombinedStatusChange,
  onReset,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as InviteCombinedStatus | '';
    if (value === '') {
      onCombinedStatusChange(undefined);
    } else {
      onCombinedStatusChange(value);
    }
  };

  const hasActiveFilters = search || combinedStatus !== undefined;

  return (
    <HStack
      spacing={4}
      p={4}
      bg={bgColor}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      wrap="wrap"
    >
      {/* Search */}
      <InputGroup maxW="300px">
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by email..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </InputGroup>

      {/* Combined Status filter */}
      <Select
        placeholder="All statuses"
        maxW="180px"
        value={combinedStatus || ''}
        onChange={handleStatusChange}
        icon={<Icon as={MdFilterList} />}
      >
        <option value="pending">Pending</option>
        <option value="accepted">Accepted</option>
        <option value="revoked">Revoked</option>
        <option value="expired">Expired</option>
      </Select>

      {/* Reset button */}
      {hasActiveFilters && (
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<Icon as={MdClear} />}
          onClick={onReset}
        >
          Clear Filters
        </Button>
      )}
    </HStack>
  );
};

export default InviteFilters;
